"use client"

import { useGSAP } from "@gsap/react"
import gsap from "gsap"
import { usePathname, useSearchParams } from "next/navigation"
import { useEffect, useRef } from "react"

import { Loader } from "@workspace/ui/components/loader"

gsap.registerPlugin(useGSAP)

/** Navigations that settle faster than this never show the bar at all. */
const SHOW_DELAY = 0.15
/** …and the page overlay waits a little longer, so quick hops never dim. */
const OVERLAY_DELAY = 0.3
/** Give up on a navigation that never lands (error, same-URL redirect). */
const MAX_PENDING_MS = 15_000

/**
 * Client-side navigation feedback: a slim top-of-viewport progress bar plus a
 * light page overlay with the animated restaurant `Loader` and a "Loading…"
 * caption.
 *
 * (store) routes have no per-route `loading.tsx` (see `(store)/page-loading.tsx`):
 * a soft navigation keeps the current page on screen until the next one is
 * ready, so this bar is what tells the diner their click registered. Next.js
 * exposes no router events, so it starts on a primary click of a same-origin
 * `<a>` that leaves the current URL (Link renders one) and finishes when the
 * pathname/search params change. Programmatic `router.push` stays silent.
 *
 * The bar trickles toward ~90% while pending, completes and fades on arrival;
 * the overlay fades in a beat later and out on arrival. The overlay is
 * `pointer-events-none`, so the diner can still click elsewhere (the new
 * navigation simply supersedes the pending one) and it can never trap the page.
 * Under reduced motion the bar is static and nothing fades (the `Loader`
 * already stops spinning under `motion-reduce`).
 */
export function NavigationProgress({
  label,
}: {
  /** Localized "Loading…" — passed in because this mounts outside the intl provider. */
  label: string
}) {
  const barRef = useRef<HTMLDivElement>(null)
  const overlayRef = useRef<HTMLDivElement>(null)
  // Set once the GSAP context exists; the route-change effect calls it.
  const finishRef = useRef<() => void>(() => {})
  const pathname = usePathname()
  const searchParams = useSearchParams()

  useGSAP((_context, contextSafe) => {
    const bar = barRef.current
    const overlay = overlayRef.current
    let pending = false
    let reducedMotion = false
    let tl: gsap.core.Timeline | null = null
    let overlayTween: gsap.core.Tween | null = null
    let timeout: ReturnType<typeof setTimeout> | undefined

    gsap.set(bar, { scaleX: 0, autoAlpha: 0 })
    gsap.set(overlay, { autoAlpha: 0 })
    gsap.matchMedia().add("(prefers-reduced-motion: reduce)", () => {
      reducedMotion = true
      return () => {
        reducedMotion = false
      }
    })

    const finish = contextSafe!(() => {
      if (!pending) return
      pending = false
      clearTimeout(timeout)
      tl?.kill()
      overlayTween?.kill()
      overlayTween = gsap.to(overlay, {
        autoAlpha: 0,
        duration: reducedMotion ? 0 : 0.2,
      })
      // Never became visible (fast navigation) → reset without a completion flash.
      if (gsap.getProperty(bar, "opacity") === 0) {
        gsap.set(bar, { scaleX: 0 })
        return
      }
      tl = gsap
        .timeline()
        .to(bar, { scaleX: 1, duration: reducedMotion ? 0 : 0.2 })
        .to(bar, { autoAlpha: 0, duration: 0.25 })
        .set(bar, { scaleX: 0 })
    })

    const start = contextSafe!(() => {
      pending = true
      clearTimeout(timeout)
      timeout = setTimeout(finish, MAX_PENDING_MS)
      tl?.kill()
      gsap.set(bar, { scaleX: 0, autoAlpha: 0 })
      overlayTween?.kill()
      gsap.set(overlay, { autoAlpha: 0 })
      overlayTween = gsap.to(overlay, {
        autoAlpha: 1,
        delay: OVERLAY_DELAY,
        duration: reducedMotion ? 0 : 0.2,
      })
      tl = gsap.timeline({ delay: SHOW_DELAY }).set(bar, { autoAlpha: 1 })
      if (reducedMotion) {
        tl.set(bar, { scaleX: 1 })
        return
      }
      tl.to(bar, { scaleX: 0.35, duration: 0.4, ease: "power2.out" }).to(bar, {
        scaleX: 0.9,
        duration: 8,
        ease: "power1.out",
      })
    })

    const onClick = (event: MouseEvent) => {
      if (
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      )
        return
      const anchor = (event.target as Element | null)?.closest?.("a[href]")
      if (!(anchor instanceof HTMLAnchorElement)) return
      if (anchor.target && anchor.target !== "_self") return
      if (anchor.hasAttribute("download")) return
      const url = new URL(anchor.href, window.location.href)
      if (url.origin !== window.location.origin) return
      // Same page (incl. hash-only jumps like the category quick-nav): no
      // navigation will land to finish the bar.
      if (
        url.pathname === window.location.pathname &&
        url.search === window.location.search
      )
        return
      start()
    }

    finishRef.current = finish
    // Capture phase: next/link calls preventDefault() on the clicks it handles,
    // so a bubble-phase listener couldn't tell them apart from cancelled ones.
    document.addEventListener("click", onClick, true)
    window.addEventListener("popstate", finish)
    return () => {
      document.removeEventListener("click", onClick, true)
      window.removeEventListener("popstate", finish)
      clearTimeout(timeout)
      finishRef.current = () => {}
    }
  })

  useEffect(() => {
    finishRef.current()
  }, [pathname, searchParams])

  return (
    <>
      <div
        ref={overlayRef}
        aria-hidden="true"
        className="pointer-events-none invisible fixed inset-0 z-90 grid place-items-center bg-background/40 opacity-0 supports-backdrop-filter:backdrop-blur-[2px]"
      >
        <div className="flex flex-col items-center gap-3">
          <Loader size={56} label={label} />
          <p className="text-sm font-medium text-foreground">{label}</p>
        </div>
      </div>
      <div
        ref={barRef}
        aria-hidden="true"
        className="pointer-events-none invisible fixed inset-x-0 top-0 z-100 h-0.75 origin-left bg-primary opacity-0 shadow-[0_1px_0_var(--primary-foreground)] rtl:origin-right"
      />
    </>
  )
}
